import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import PageForm, { FormField } from '../../../../commons/PageForm';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { AlertVariant } from '../../../../commons/PageAlert';
import {
  CategoriaActividadPoliticaVencimientoDTO,
  AJUSTE_DIA_NO_HABIL_OPTIONS,
  defaultPoliticaForm,
  dtoToForm,
  formToDto,
  getDiasVencimientoBaseHint,
  isDiaVencimientoBase,
  isTipoLegacyRelativo,
  OCURRENCIA_OPTIONS,
  POLITICA_VENCIMIENTO_API,
  PoliticaVencimientoForm,
  PoliticaVencimientoTipo,
  TIPO_POLITICA_OPTIONS,
  validatePoliticaForm,
  DIA_SEMANA_OPTIONS,
} from '../../../../shared/politica-vencimiento';

function extractApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail ?? err.response?.data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return 'No se pudo completar la operación.';
}

interface CategoriaActividadVencimientoTabProps {
  categoriaActividadId: string;
  onBack: () => void;
  onNotify: (variant: AlertVariant, message: string) => void;
}

export default function CategoriaActividadVencimientoTab({
  categoriaActividadId,
  onBack,
  onNotify,
}: CategoriaActividadVencimientoTabProps) {
  const [form, setForm] = useState<PoliticaVencimientoForm>(defaultPoliticaForm());
  const [errors, setErrors] = useState<Partial<Record<keyof PoliticaVencimientoForm, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [audit, setAudit] = useState<{ updated?: string; created?: string }>({});

  const { confirmConfig, ask, dismiss } = useConfirm();

  const loadPolitica = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<CategoriaActividadPoliticaVencimientoDTO>(
        POLITICA_VENCIMIENTO_API(categoriaActividadId),
      );
      setForm(dtoToForm(res.data));
      setAudit({ updated: res.data.updated, created: res.data.created });
      setLoaded(true);
    } catch (err) {
      onNotify('error', extractApiError(err));
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [categoriaActividadId, onNotify]);

  useEffect(() => {
    loadPolitica();
  }, [loadPolitica]);

  const handleFieldChange = (name: keyof PoliticaVencimientoForm, value: string) => {
    setForm(prev => {
      const next = { ...prev, [name]: value };

      if (name === 'tipo') {
        const tipo = value as PoliticaVencimientoTipo;
        if (isDiaVencimientoBase(tipo)) {
          next.dias = '0';
        } else if (isTipoLegacyRelativo(tipo)) {
          next.dias = '3';
          next.ajusteDiaNoHabil = 'NINGUNO';
        } else if (next.ajusteDiaNoHabil === 'NINGUNO') {
          next.ajusteDiaNoHabil = 'DIA_HABIL_SIGUIENTE';
        }
      }

      return next;
    });
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validatePoliticaForm(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const res = await axios.put<CategoriaActividadPoliticaVencimientoDTO>(
        POLITICA_VENCIMIENTO_API(categoriaActividadId),
        formToDto(form),
      );
      setForm(dtoToForm(res.data));
      setAudit({ updated: res.data.updated, created: res.data.created });
      onNotify('success', 'Política de vencimiento guardada correctamente.');
    } catch (err) {
      onNotify('error', extractApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    ask(
      <>¿Confirmas que deseas eliminar la política de vencimiento de esta categoría? Se volverá a crear una política por defecto al recargar.</>,
      async () => {
        setDeleting(true);
        try {
          await axios.delete(POLITICA_VENCIMIENTO_API(categoriaActividadId));
          dismiss();
          onNotify('success', 'Política de vencimiento eliminada correctamente.');
          await loadPolitica();
        } catch (err) {
          onNotify('error', extractApiError(err));
        } finally {
          setDeleting(false);
        }
      },
      { title: 'Eliminar política de vencimiento', confirmLabel: 'Eliminar', variant: 'danger' },
    );
  };

  const tipo = form.tipo;
  const diasVencimientoBaseHint = getDiasVencimientoBaseHint(form.dias);

  const fields: FormField<PoliticaVencimientoForm>[] = useMemo(() => {
    const base: FormField<PoliticaVencimientoForm>[] = [
      {
        name: 'aplicar',
        label: 'Aplicar regla',
        type: 'checkbox',
        hint: 'Activa o desactiva la regla de vencimiento para esta categoría',
      },
      {
        name: 'nombre',
        label: 'Nombre',
        required: true,
        placeholder: 'Nombre único de la regla',
      },
      {
        name: 'descripcion',
        label: 'Descripción',
        type: 'textarea',
        rows: 2,
        placeholder: 'Descripción opcional para la UI',
        fullWidth: true,
      },
      {
        name: 'tipo',
        label: 'Tipo de regla',
        type: 'select',
        required: true,
        options: [...TIPO_POLITICA_OPTIONS],
      },
    ];

    if (tipo === 'DIA_PERIODO') {
      base.push(
        {
          name: 'diaPeriodo',
          label: 'Día del mes',
          type: 'number',
          required: true,
          placeholder: '1–31',
          hint: 'Día fijo del mes del periodo',
        },
        {
          name: 'ajusteDiaNoHabil',
          label: 'Ajuste día no hábil',
          type: 'select',
          required: true,
          options: [...AJUSTE_DIA_NO_HABIL_OPTIONS],
          hint: 'Si la fecha cae en fin de semana o feriado',
        },
      );
    }

    if (tipo === 'DIA_SEMANA_MES') {
      base.push(
        {
          name: 'ocurrencia',
          label: 'Ocurrencia',
          type: 'select',
          required: true,
          options: [...OCURRENCIA_OPTIONS],
        },
        {
          name: 'diaSemana',
          label: 'Día de la semana',
          type: 'select',
          required: true,
          options: [...DIA_SEMANA_OPTIONS],
        },
        {
          name: 'ajusteDiaNoHabil',
          label: 'Ajuste día no hábil',
          type: 'select',
          required: true,
          options: [...AJUSTE_DIA_NO_HABIL_OPTIONS],
        },
      );
    }

    if (isDiaVencimientoBase(tipo)) {
      base.push({
        name: 'dias',
        label: 'Desplazamiento (días hábiles)',
        type: 'number',
        required: false,
        placeholder: 'Ej: 0, -3, 2 (vacío = 0)',
        hint: diasVencimientoBaseHint ?? '0 = vencimiento SAT exacto; negativo = antes; positivo = después',
      });
    }

    if (isTipoLegacyRelativo(tipo)) {
      base.push({
        name: 'dias',
        label: 'Días hábiles',
        type: 'number',
        required: true,
        placeholder: 'Ej: 3',
        hint: tipo === 'VENCIMIENTO_MENOS_N'
          ? 'Días hábiles antes del vencimiento SAT (legacy)'
          : 'Días hábiles después del vencimiento SAT (legacy)',
      });
    }

    return base;
  }, [tipo, diasVencimientoBaseHint]);

  if (loading) {
    return <div style={s.loadingBox}>Cargando política de vencimiento…</div>;
  }

  if (!loaded) {
    return <div style={s.loadingBox}>No se pudo cargar la política de vencimiento.</div>;
  }

  return (
    <>
      <Confirm config={confirmConfig} />

      {audit.updated && (
        <div style={s.auditBox}>
          Última modificación: {new Date(audit.updated).toLocaleString()}
        </div>
      )}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={saving}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onBack}
        accentColor="#f59e0b"
        submitLabel="Guardar política"
        actionSlotAfterCancel={(
          <button
            type="button"
            style={s.deleteBtn}
            onClick={handleDelete}
            disabled={deleting || saving}
          >
            <FaTrash style={{ marginRight: 6 }} />
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        )}
      />
    </>
  );
}

const s: Record<string, React.CSSProperties> = {
  loadingBox: {
    padding: '32px 24px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#64748b',
    fontSize: 14,
    textAlign: 'center',
  },
  auditBox: {
    padding: '8px 12px',
    marginBottom: 12,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    color: '#64748b',
    fontSize: 12,
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 14px',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    background: '#fff1f2',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
